import React, { useEffect, useRef, useState } from "react";
import Header from "../miniapps/homeAiStore/components/Header";
import Sidebar from "../miniapps/homeAiStore/components/Sidebar";
import HeroSection from "../miniapps/homeAiStore/components/HeroSection";
import ToolsSection from "../miniapps/homeAiStore/components/ToolsSection";
import ChatAssistantWindow from "../miniapps/assistant/components/ChatAssistantWindow";
import ComingSoonPage from "../miniapps/veevaGpt/pages/ComingSoonPage";
import VeevaGptPage from "../miniapps/veevaGpt/pages/VeevaGptPage";
import VaultCRMPage from "../miniapps/vaultMigration/pages/VaultCRMPage";
import MigrationWizard from "../miniapps/vaultMigration/pages/MigrationWizard";
import DocRedactPage from "../miniapps/docRedact/pages/DocRedactPage";
import DocsComparePage from "../miniapps/docsCompare/pages/DocsComparePage";
import DocTemplateConverterPage from "../miniapps/docTemplateConverter/pages/DocTemplateConverterPage";
import UniversalPatientLanguagePage from "../miniapps/universalPatientLanguage/pages/UniversalPatientLanguagePage";
import Footer from "../miniapps/homeAiStore/components/Footer";
import LoginPage from "../miniapps/auth/pages/LoginPage";
import ChangePasswordPage from "../miniapps/auth/pages/ChangePasswordPage";
import AdminUsersPage from "../miniapps/auth/pages/AdminUsersPage";
import PrivacyStatement from "../miniapps/auth/components/PrivacyStatement";
import { ThemeProvider, useTheme } from "../contexts/ThemeContext";
import { preferenceService } from "../services/preferenceService";
import { getApiBaseUrl } from "./utils/http";
import {
  authFetchWithTimeout,
  authFetch,
  clearTokens,
  isTokenValid,
  getAuthUser,
  isAuthenticated as checkAuth,
  logoutApi,
} from "./utils/auth";
import { normalizeReasoning } from "../miniapps/assistant/utils/assistantReasoning";
import "../styles.css";
import "./host.css";
import "../miniapps/homeAiStore/homeAiStore.css";

const AUTH_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const AUTH_LAST_ACTIVITY_KEY = "authLastActivityAt";

const createClientId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

function AppHost() {
  const API_BASE_URL = getApiBaseUrl();

  const [currentPage, setCurrentPage] = useState("home");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [sessionId, setSessionId] = useState(() => createClientId());
  const [isChatView, setIsChatView] = useState(false);
  const [homeViewKey, setHomeViewKey] = useState(0);
  const [migrationWizardKey, setMigrationWizardKey] = useState(0);
  const [veevaGptMessages, setVeevaGptMessages] = useState([]);
  const [veevaGptConversationId, setVeevaGptConversationId] = useState(null);
  const [veevaGptChatHistory, setVeevaGptChatHistory] = useState([]);
  const [veevaGptProjects, setVeevaGptProjects] = useState([]);
  const [veevaGptActiveProjectId, setVeevaGptActiveProjectId] = useState(null);
  const [veevaGptAvailableModels, setVeevaGptAvailableModels] = useState([]);
  const [veevaGptSelectedModel, setVeevaGptSelectedModel] = useState(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [theme, setTheme] = useState("light");
  const [profileNameRaw, setProfileNameRaw] = useState(
    () => localStorage.getItem("profileNameRaw") || localStorage.getItem("profileName") || "Admin"
  );
  const [profileName, setProfileName] = useState(() => toTitleCase(localStorage.getItem("profileName") || "Admin") || "Admin");
  const [isAuthenticated, setIsAuthenticated] = useState(() => checkAuth());
  const [showPrivacyStatement, setShowPrivacyStatement] = useState(false);
  const [privacyReadOnly, setPrivacyReadOnly] = useState(false);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [contactEmail, setContactEmail] = useState("");

  // Load user preferences and app layout config on authentication
  useEffect(() => {
    const loadPreferences = async () => {
      if (isAuthenticated) {
        try {
          const prefs = await preferenceService.getPreferences();
          if (prefs.theme) {
            setTheme(prefs.theme === 'auto' ? 'light' : prefs.theme);
          }
        } catch (error) {
          console.error('Failed to load preferences:', error);
        }
        try {
          const res = await authFetch(`${getApiBaseUrl()}/home-ai-store/layout`);
          if (res.ok) {
            const layout = await res.json();
            if (layout.support_email) setContactEmail(layout.support_email);
          }
        } catch (error) {
          console.error('Failed to load layout config:', error);
        }
      } else {
        setTheme('light');
        setContactEmail("");
      }
    };
    loadPreferences();
  }, [isAuthenticated]);

  useEffect(() => {
    // Apply theme to document
    if (!isAuthenticated) {
      document.documentElement.setAttribute("data-theme", "light");
      return;
    }
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme, isAuthenticated]);

  useEffect(() => {
    // Check privacy statement acceptance for already authenticated users (per-user)
    if (isAuthenticated && profileNameRaw) {
      const CURRENT_PRIVACY_VERSION = "1.2.0";
      const acceptedVersion = localStorage.getItem(`privacyAcceptedVersion_${profileNameRaw}`);
      const privacyAccepted = localStorage.getItem(`privacyAccepted_${profileNameRaw}`) === "true";

      console.log(`[Privacy] useEffect check: user=${profileNameRaw}, accepted=${privacyAccepted}, version=${acceptedVersion}, required=${CURRENT_PRIVACY_VERSION}`);

      if (!privacyAccepted || acceptedVersion !== CURRENT_PRIVACY_VERSION) {
        console.log('[Privacy] Showing privacy statement (useEffect)');
        setShowPrivacyStatement(true);
      }
    }
  }, [isAuthenticated, profileNameRaw]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    if (localStorage.getItem("isAuthenticated") === "true") {
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
    }
  }, [isAuthenticated]);

  // Listen for auth:logout events dispatched by authFetch on 401 failures
  useEffect(() => {
    const onAuthLogout = () => {
      clearTokens();
      setIsAuthenticated(false);
      setMustResetPassword(false);
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
      // Clear profile data (dynamically, not hardcoded to admin)
      setProfileNameRaw("");
      localStorage.removeItem("profileNameRaw");
      setProfileName("");
      localStorage.removeItem("profileName");
      setCurrentPage("home");
      setSearchQuery("");
      setConversationId(null);
      setChatMessages([]);
      setIsChatView(false);
      setIsSidebarOpen(false);
      setTheme("light");
    };
    window.addEventListener("auth:logout", onAuthLogout);
    return () => window.removeEventListener("auth:logout", onAuthLogout);
  }, []);

  const ensureConversation = async () => {
    if (conversationId) {
      return conversationId;
    }

    const generatedConversationId = createClientId();
    setConversationId(generatedConversationId);
    return generatedConversationId;
  };

  const handleSearch = async (query, options = {}) => {
    const trimmedQuery = query.trim();
    const normalizedQuery = trimmedQuery.toLowerCase();

    if (normalizedQuery === "new chat") {
      handleClearSearch();
      return;
    }

    const cleanedQuery = trimmedQuery.replace(/^new\s*chat[:\-\s]*/i, "").trim();
    const fromChat = Boolean(options?.fromChat);
    const fromHomeSubmit = Boolean(options?.fromHomeSubmit);

    if (!fromChat && !fromHomeSubmit) {
      setSearchQuery(cleanedQuery);
    }
    
    if (!cleanedQuery) {
      setIsChatView(false);
      return;
    }

    const timestamp = Date.now();
    const pendingAssistantId = `assistant-pending-${timestamp}`;

    setChatMessages((previous) => [
      ...previous,
      {
        id: `user-${timestamp}`,
        role: "user",
        text: cleanedQuery
      },
      {
        id: pendingAssistantId,
        role: "assistant",
        title: "Migration Strategy Assistant",
        answer: "",
        reasoning: [],
        highlights: [],
        actions: [],
        actionsReady: false,
        isLoading: true
      }
    ]);

    setIsChatView(true);
    if (fromChat || fromHomeSubmit) {
      setSearchQuery("");
    }

    try {
      const activeConversationId = await ensureConversation();
      const response = await authFetchWithTimeout(`${API_BASE_URL}/assistant/messages/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          conversation_id: activeConversationId,
          message: cleanedQuery,
          context: {
            current_page: currentPage,
            tool_scope: currentPage
          }
        })
      }, 180000);

      if (!response.ok) {
        throw new Error("Failed to fetch assistant response");
      }

      if (!response.body) {
        throw new Error("Streaming response body is empty");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamedAnswer = "";
      let streamedReasoningText = "";
      let streamedReasoningSteps = [];
      let streamedDisclaimer = "";

      const appendAssistantChunk = (updater) => {
        setChatMessages((previous) =>
          previous.map((item) => {
            if (item.id !== pendingAssistantId) {
              return item;
            }
            return updater(item);
          })
        );
      };

      const handleStreamEvent = (eventPayload) => {
        if (eventPayload.type === "answer_delta") {
          streamedAnswer += eventPayload.delta || "";
          appendAssistantChunk((item) => ({
            ...item,
            answer: streamedAnswer,
            isLoading: true
          }));
          return;
        }

        if (eventPayload.type === "reasoning_step") {
          // reasoning_step contains the complete reasoning — replace any
          // partial reasoning that was accumulated from reasoning_delta events
          const stepParts = normalizeReasoning(eventPayload.step);
          if (stepParts.length === 0) {
            return;
          }

          streamedReasoningSteps = stepParts;
          appendAssistantChunk((item) => ({
            ...item,
            reasoning: streamedReasoningSteps,
            isLoading: true
          }));
          return;
        }

        if (eventPayload.type === "reasoning_delta") {
          streamedReasoningText += eventPayload.delta || "";
          const dynamicSteps = normalizeReasoning(streamedReasoningText);
          if (dynamicSteps.length > 0) {
            streamedReasoningSteps = dynamicSteps;
            appendAssistantChunk((item) => ({
              ...item,
              reasoning: dynamicSteps,
              isLoading: true
            }));
          }
          return;
        }

        if (eventPayload.type === "done") {
          appendAssistantChunk((item) => ({
            ...item,
            title: eventPayload.title || item.title || "AI Assistant",
            answer: eventPayload.answer || streamedAnswer || item.answer || "",
            reasoning: (() => {
              const finalReasoning = normalizeReasoning(eventPayload.reasoning);
              if (finalReasoning.length > 0) {
                return finalReasoning;
              }
              if (streamedReasoningSteps.length > 0) {
                return streamedReasoningSteps;
              }
              const fallbackDynamic = normalizeReasoning(streamedReasoningText);
              return fallbackDynamic.length > 0 ? fallbackDynamic : item.reasoning || [];
            })(),
            highlights: eventPayload.highlights || item.highlights || [],
            actions: eventPayload.actions || item.actions || [],
            actionsReady: false,
            isLoading: false
          }));
          return;
        }

        if (eventPayload.type === "disclaimer") {
          const text = (eventPayload.text || "").trim();
          if (!text) {
            return;
          }

          appendAssistantChunk((item) => ({
            ...item,
            highlights: [text],
            actionsReady: true
          }));
          return;
        }

        if (eventPayload.type === "disclaimer_delta") {
          streamedDisclaimer += eventPayload.delta || "";
          const dynamicText = streamedDisclaimer.trim();
          if (!dynamicText) {
            return;
          }
          appendAssistantChunk((item) => ({
            ...item,
            highlights: [dynamicText]
          }));
          return;
        }

        if (eventPayload.type === "action_update") {
          const streamedActions = Array.isArray(eventPayload.actions) ? eventPayload.actions : [];
          appendAssistantChunk((item) => ({
            ...item,
            actions: streamedActions
          }));
          return;
        }

        if (eventPayload.type === "error") {
          appendAssistantChunk((item) => ({
            ...item,
            isLoading: false,
            highlights: [eventPayload.message || "Streaming error"]
          }));
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        events.forEach((rawEvent) => {
          const trimmed = rawEvent.trim();
          if (!trimmed.startsWith("data:")) {
            return;
          }

          const jsonPayload = trimmed.replace(/^data:\s*/, "");
          try {
            const parsed = JSON.parse(jsonPayload);
            handleStreamEvent(parsed);
          } catch (error) {
            console.debug("Failed to parse stream event", error);
          }
        });
      }

      setChatMessages((previous) =>
        previous.map((item) =>
          item.id === pendingAssistantId
            ? {
                ...item,
                isLoading: false
              }
            : item
        )
      );
    } catch (error) {
      const isTimeout = error?.name === "AbortError";
      const highlightMessage = isTimeout
        ? "Assistant request timed out. Please verify backend availability and try again."
        : error?.message || "Unknown error";
      setChatMessages((previous) =>
        previous.map((item) =>
          item.id === pendingAssistantId
            ? {
                ...item,
                title: "Assistant Unavailable",
                answer: "Backend assistant request failed. Please verify backend is running on localhost:8000.",
                reasoning: ["Frontend could not complete the /assistant/messages/stream API call."],
                highlights: [highlightMessage],
                actions: [],
                actionsReady: false,
                isLoading: false
              }
            : item
        )
      );
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setChatMessages([]);
    setConversationId(null);
    setIsChatView(false);
  };

  const handleChatAction = (targetPage) => {
    setConversationId(null);
    setSearchQuery("");
    setChatMessages([]);
    setIsChatView(false);
    setCurrentPage(targetPage);
  };

  const handleQuickActionPrefill = (prompt) => {
    const cleanedPrompt = (prompt || "").trim();
    setSearchQuery(cleanedPrompt);

    if (!cleanedPrompt) {
      setIsChatView(false);
      return;
    }

    setIsChatView(true);
  };

  const handleToolSelect = (toolId) => {
    setConversationId(null);
    setSearchQuery("");
    setChatMessages([]);
    setIsChatView(false);

    if (toolId === "veeva-gpt") {
      setCurrentPage("veeva-gpt");
      // Load conversations and projects from Veeva GPT backend (cookie auth)
      // Use .catch to silently handle failures — user can still use the page
      authFetch(`${API_BASE_URL}/veeva-gpt/conversations`)
        .then((r) => (r && r.ok ? r.json() : { conversations: [] }))
        .catch(() => ({ conversations: [] }))
        .then((convData) => {
          setVeevaGptChatHistory(((convData || {}).conversations || []).map((c) => ({
            id: c.id,
            title: c.title,
            project_id: c.project_id || null,
            active: false,
          })));
        })
        .catch(() => {});
      authFetch(`${API_BASE_URL}/veeva-gpt/projects`)
        .then((r) => (r && r.ok ? r.json() : { projects: [] }))
        .catch(() => ({ projects: [] }))
        .then((data) => {
          setVeevaGptProjects(((data || {}).projects || []).map((p) => ({
            id: p.id,
            name: p.name,
            chat_count: p.chat_count || 0,
          })));
        })
        .catch(() => {});
      authFetch(`${API_BASE_URL}/veeva-gpt/models`)
        .then((r) => (r && r.ok ? r.json() : { models: [], default_model: "gpt-4.1" }))
        .catch(() => ({ models: [], default_model: "gpt-4.1" }))
        .then((data) => {
          const models = (data || {}).models || [];
          setVeevaGptAvailableModels(models);
          const def = (data || {}).default_model || "gpt-4.1";
          setVeevaGptSelectedModel((prev) => prev || def);
        })
        .catch(() => {});
    } else if (toolId === "veeva-vault-migration") {
      setCurrentPage("vault-crm");
    } else if (toolId === "doc-redact") {
      setCurrentPage("doc-redact");
    } else if (toolId === "docs-compare") {
      setCurrentPage("docs-compare");
    } else if (toolId === "doc-template-converter") {
      setCurrentPage("doc-template-converter");
    } else if (toolId === "universal-patient-language") {
      setCurrentPage("universal-patient-language");
    }
  };

  const handleVeevaGptSubmit = async (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const timestamp = Date.now();
    const pendingId = `vgpt-assistant-${timestamp}`;

    setVeevaGptMessages((prev) => [
      ...prev,
      { id: `vgpt-user-${timestamp}`, role: "user", text: trimmed },
      {
        id: pendingId,
        role: "assistant",
        title: "Veeva GPT",
        answer: "",
        reasoning: [],
        highlights: [],
        isLoading: true
      }
    ]);

    const activeConvId = veevaGptConversationId || createClientId();
    if (!veevaGptConversationId) {
      setVeevaGptConversationId(activeConvId);
    }

    // Update chat history
    setVeevaGptChatHistory((prev) => {
      const existing = prev.find((c) => c.id === activeConvId);
      if (existing) {
        return prev.map((c) => ({ ...c, active: c.id === activeConvId }));
      }
      const title = trimmed.length > 40 ? trimmed.slice(0, 40) + "..." : trimmed;
      return [
        { id: activeConvId, title, project_id: veevaGptActiveProjectId || null, active: true },
        ...prev.map((c) => ({ ...c, active: false }))
      ];
    });

    try {
      // Use Veeva GPT streaming endpoint
      const response = await authFetchWithTimeout(`${API_BASE_URL}/veeva-gpt/messages/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: trimmed,
          conversation_id: activeConvId,
          session_id: sessionId,
          model: veevaGptSelectedModel || undefined
        })
      }, 90000);

      if (!response.ok) throw new Error("Failed to fetch Veeva GPT response");
      if (!response.body) throw new Error("Streaming response body is empty");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamedAnswer = "";
      let streamedReasoningText = "";
      let streamedReasoningSteps = [];
      let streamedDisclaimer = "";

      const appendChunk = (updater) => {
        setVeevaGptMessages((prev) =>
          prev.map((item) => item.id === pendingId ? updater(item) : item)
        );
      };

      const handleEvent = (eventPayload) => {
        if (eventPayload.type === "answer_delta") {
          streamedAnswer += eventPayload.delta || "";
          appendChunk((item) => ({ ...item, answer: streamedAnswer, isLoading: true }));
          return;
        }
        if (eventPayload.type === "sources") {
          // Handle sources from RAG endpoint
          const sources = eventPayload.sources || [];
          if (sources.length > 0) {
            const sourceInfo = `Sources: ${sources.map(s => s.title).join(", ")}`;
            appendChunk((item) => ({ ...item, highlights: [sourceInfo], isLoading: true }));
          }
          return;
        }
        if (eventPayload.type === "reasoning_step") {
          // reasoning_step contains the complete reasoning — replace any
          // partial reasoning that was accumulated from reasoning_delta events
          const stepParts = normalizeReasoning(eventPayload.step);
          if (stepParts.length === 0) return;
          streamedReasoningSteps = stepParts;
          appendChunk((item) => ({ ...item, reasoning: streamedReasoningSteps, isLoading: true }));
          return;
        }
        if (eventPayload.type === "reasoning_delta") {
          streamedReasoningText += eventPayload.delta || "";
          const dynamicSteps = normalizeReasoning(streamedReasoningText);
          if (dynamicSteps.length > 0) {
            streamedReasoningSteps = dynamicSteps;
            appendChunk((item) => ({ ...item, reasoning: dynamicSteps, isLoading: true }));
          }
          return;
        }
        if (eventPayload.type === "done") {
          appendChunk((item) => ({
            ...item,
            title: eventPayload.title || "Veeva GPT",
            answer: eventPayload.answer || streamedAnswer || "",
            reasoning: (() => {
              const fr = normalizeReasoning(eventPayload.reasoning);
              if (fr.length > 0) return fr;
              if (streamedReasoningSteps.length > 0) return streamedReasoningSteps;
              const fb = normalizeReasoning(streamedReasoningText);
              return fb.length > 0 ? fb : item.reasoning || [];
            })(),
            highlights: eventPayload.highlights || [],
            isLoading: false
          }));
          return;
        }
        if (eventPayload.type === "disclaimer_delta") {
          streamedDisclaimer += eventPayload.delta || "";
          const text = streamedDisclaimer.trim();
          if (text) appendChunk((item) => ({ ...item, highlights: [text] }));
          return;
        }
        if (eventPayload.type === "disclaimer") {
          const text = (eventPayload.text || "").trim();
          if (text) appendChunk((item) => ({ ...item, highlights: [text] }));
          return;
        }
        if (eventPayload.type === "error") {
          appendChunk((item) => ({ ...item, isLoading: false, highlights: [eventPayload.message || "Streaming error"] }));
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        events.forEach((rawEvent) => {
          const trimmedEvt = rawEvent.trim();
          if (!trimmedEvt.startsWith("data:")) return;
          const jsonPayload = trimmedEvt.replace(/^data:\s*/, "");
          try {
            handleEvent(JSON.parse(jsonPayload));
          } catch (e) {
            console.debug("Failed to parse Veeva GPT stream event", e);
          }
        });
      }

      setVeevaGptMessages((prev) =>
        prev.map((item) => item.id === pendingId ? { ...item, isLoading: false } : item)
      );

      // Refresh chat history from Veeva GPT backend after message completes
      try {
        const historyRes = await authFetch(`${API_BASE_URL}/veeva-gpt/conversations`);
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          const items = (historyData.conversations || []).map((c) => ({
            id: c.id,
            title: c.title,
            project_id: c.project_id || null,
            active: c.id === activeConvId
          }));
          setVeevaGptChatHistory(items);
        }
      } catch (_) {}
      // Refresh projects
      try {
        const projRes = await authFetch(`${API_BASE_URL}/veeva-gpt/projects`);
        if (projRes.ok) {
          const projData = await projRes.json();
          setVeevaGptProjects((projData.projects || []).map((p) => ({
            id: p.id,
            name: p.name,
            chat_count: p.chat_count || 0,
          })));
        }
      } catch (_) {}
    } catch (error) {
      const isTimeout = error?.name === "AbortError";
      const msg = isTimeout
        ? "Veeva GPT request timed out. Please try again."
        : error?.message || "Unknown error";
      setVeevaGptMessages((prev) =>
        prev.map((item) =>
          item.id === pendingId
            ? { ...item, answer: "Request failed. Please verify backend is running.", reasoning: [], highlights: [msg], isLoading: false }
            : item
        )
      );
    }
  };

  const handleVeevaGptNewChat = () => {
    setVeevaGptConversationId(null);
    setVeevaGptMessages([]);
    setVeevaGptChatHistory((prev) => prev.map((c) => ({ ...c, active: false })));
    setVeevaGptActiveProjectId(null);
  };



  const handleVeevaGptDeleteChat = async (chatId) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/conversations/${chatId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setVeevaGptChatHistory((prev) => prev.filter((c) => c.id !== chatId));
        if (veevaGptConversationId === chatId) {
          setVeevaGptConversationId(null);
          setVeevaGptMessages([]);
        }
      }
    } catch (err) {
      console.error("Failed to delete chat", err);
    }
  };

  const handleVeevaGptRenameChat = async (chatId, newTitle) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/conversations/${chatId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
      if (res.ok) {
        setVeevaGptChatHistory((prev) => prev.map((c) => c.id === chatId ? { ...c, title: newTitle } : c));
      }
    } catch (err) {
      console.error("Failed to rename chat", err);
    }
  };

  const handleVeevaGptSelectChat = async (chatId) => {
    setVeevaGptChatHistory((prev) => prev.map((c) => ({ ...c, active: c.id === chatId })));
    setVeevaGptConversationId(chatId);
    // Find project_id for this chat
    const chatEntry = (veevaGptChatHistory || []).find((c) => c.id === chatId);
    setVeevaGptActiveProjectId(chatEntry?.project_id || null);
    // Fetch messages for the selected conversation
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/conversations/${chatId}`);
      if (!res.ok) {
        if (res.status === 403) {
          setVeevaGptMessages([{ id: "access-denied", role: "assistant", answer: "You do not have access to this conversation.", reasoning: [], highlights: [], isLoading: false }]);
          return;
        }
        throw new Error("Failed to fetch conversation");
      }
      const data = await res.json();
      const loadedMessages = (data.messages || []).map((msg, idx) => {
        if (msg.role === "user") {
          return { id: `loaded-user-${chatId}-${idx}`, role: "user", text: msg.content };
        }
        return {
          id: `loaded-assistant-${chatId}-${idx}`,
          role: "assistant",
          title: "Veeva GPT",
          answer: msg.content || "",
          reasoning: [],
          highlights: [],
          isLoading: false
        };
      });
      setVeevaGptMessages(loadedMessages);
    } catch (err) {
      console.error("Failed to load conversation", err);
      setVeevaGptMessages([]);
    }
  };

  // ------------------------------------------------------------------
  // Project (Cluster) handlers for Veeva GPT
  // ------------------------------------------------------------------

  const handleVeevaGptCreateProject = async (name) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        setVeevaGptProjects((prev) => [
          { id: data.project_id, name, chat_count: 0 },
          ...prev,
        ]);
      }
    } catch (err) {
      console.error("Failed to create project", err);
    }
  };

  const handleVeevaGptDeleteProject = async (projectId) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/projects/${projectId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setVeevaGptProjects((prev) => prev.filter((p) => p.id !== projectId));
        setVeevaGptChatHistory((prev) => prev.filter((c) => c.project_id !== projectId));
        if (veevaGptActiveProjectId === projectId) {
          setVeevaGptActiveProjectId(null);
          setVeevaGptConversationId(null);
          setVeevaGptMessages([]);
        }
      }
    } catch (err) {
      console.error("Failed to delete project", err);
    }
  };

  const handleVeevaGptRenameProject = async (projectId, newName) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName }),
      });
      if (res.ok) {
        setVeevaGptProjects((prev) =>
          prev.map((p) => (p.id === projectId ? { ...p, name: newName } : p))
        );
      }
    } catch (err) {
      console.error("Failed to rename project", err);
    }
  };

  const handleVeevaGptNewChatInProject = async (projectId) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/veeva-gpt/projects/${projectId}/conversations`, {
        method: "POST",
      });
      if (res.ok) {
        const data = await res.json();
        const newChatId = data.conversation_id;
        setVeevaGptConversationId(newChatId);
        setVeevaGptMessages([]);
        setVeevaGptActiveProjectId(projectId);
        setVeevaGptChatHistory((prev) => [
          { id: newChatId, title: "New Chat", project_id: projectId, active: true },
          ...prev.map((c) => ({ ...c, active: false })),
        ]);
      }
    } catch (err) {
      console.error("Failed to create chat in project", err);
    }
  };

  const handleBackToHome = () => {
    setCurrentPage("home");
    setSearchQuery("");
    setChatMessages([]);
    setConversationId(null);
    setIsChatView(false);
  };

  // ── Browser back button → home screen ─────────────────────────────
  // The app is state-routed (no URL router). We keep a single history
  // entry above "home" while on any sub-page, so pressing the browser
  // back button anywhere in the app returns to the home screen instead
  // of leaving the site.
  const historyPushedRef = useRef(false);
  useEffect(() => {
    if (currentPage !== "home") {
      if (!historyPushedRef.current) {
        window.history.pushState({ appPage: true }, "");
        historyPushedRef.current = true;
      }
    } else {
      historyPushedRef.current = false;
    }
  }, [currentPage]);

  useEffect(() => {
    const onPopState = () => {
      // Return to the home screen on browser back (uses stable state setters).
      historyPushedRef.current = false;
      setCurrentPage("home");
      setSearchQuery("");
      setChatMessages([]);
      setConversationId(null);
      setIsChatView(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleStartMigration = () => {
    setConversationId(null);
    setSearchQuery("");
    setChatMessages([]);
    setIsChatView(false);
    setMigrationWizardKey((previous) => previous + 1);
    setCurrentPage("migration-wizard");
  };

  const handleBackToVaultCRM = () => {
    setConversationId(null);
    setSearchQuery("");
    setChatMessages([]);
    setIsChatView(false);
    setCurrentPage("vault-crm");
  };

  const handleLogin = (username, authOptions = {}) => {
    const normalizedRawName = (username || "").trim() || "user";
    const normalizedDisplayName = toTitleCase(normalizedRawName) || "User";

    // Check if backend requires password reset
    if (authOptions.mustResetPassword) {
      setMustResetPassword(true);
      setProfileNameRaw(normalizedRawName);
      localStorage.setItem("profileNameRaw", normalizedRawName);
      setProfileName(normalizedDisplayName);
      localStorage.setItem("profileName", normalizedDisplayName);
      // Go straight to password reset — privacy statement will show after first real login
      return;
    }

    setMustResetPassword(false);
    setIsAuthenticated(true);
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem(AUTH_LAST_ACTIVITY_KEY, String(Date.now()));
    setProfileNameRaw(normalizedRawName);
    localStorage.setItem("profileNameRaw", normalizedRawName);
    setProfileName(normalizedDisplayName);
    localStorage.setItem("profileName", normalizedDisplayName);

    // Restore this user's saved theme preference
    const savedTheme = localStorage.getItem(`theme_${normalizedRawName}`);
    if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
      setTheme(savedTheme);
    }

    // Reset URL to root (in case we were at /login or similar)
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
    setCurrentPage("home");
    setSearchQuery("");
    setSessionId(createClientId());
    setConversationId(null);
    setChatMessages([]);
    setIsChatView(false);

    // Check if privacy statement has been accepted for current version (per-user)
    const CURRENT_PRIVACY_VERSION = "1.2.0";
    const acceptedVersion = localStorage.getItem(`privacyAcceptedVersion_${normalizedRawName}`);
    const privacyAccepted = localStorage.getItem(`privacyAccepted_${normalizedRawName}`) === "true";

    console.log(`[Privacy] handleLogin check: user=${normalizedRawName}, accepted=${privacyAccepted}, version=${acceptedVersion}, required=${CURRENT_PRIVACY_VERSION}`);

    if (!privacyAccepted || acceptedVersion !== CURRENT_PRIVACY_VERSION) {
      console.log('[Privacy] Showing privacy statement (handleLogin)');
      setShowPrivacyStatement(true);
    }
  };

  const handlePrivacyAccept = () => {
    setShowPrivacyStatement(false);
    setPrivacyReadOnly(false);
    // Privacy acceptance is handled within the PrivacyStatement component
    // which stores the acceptance in localStorage
  };

  const handleShowPrivacyFromFooter = () => {
    setPrivacyReadOnly(true);
    setShowPrivacyStatement(true);
  };

  const handlePrivacyClose = () => {
    setShowPrivacyStatement(false);
    setPrivacyReadOnly(false);
  };

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      setIsAuthenticated(false);
      setMustResetPassword(false);
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
      setProfileNameRaw("");
      localStorage.removeItem("profileNameRaw");
      setProfileName("");
      localStorage.removeItem("profileName");
      setCurrentPage("home");
      setSearchQuery("");
      setSessionId(createClientId());
      setConversationId(null);
      setChatMessages([]);
      setIsChatView(false);
      setIsSidebarOpen(false);
      setTheme("light");
    }
  };

  const handleToggleTheme = async () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);

    // Save to server if authenticated
    if (isAuthenticated) {
      try {
        await preferenceService.setTheme(newTheme);
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let timeoutId;

    const runLogout = () => {
      clearTokens();
      setIsAuthenticated(false);
      setMustResetPassword(false);
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
      // Clear privacy acceptance on auto-logout so it shows again on next login
      sessionStorage.removeItem("privacyAccepted");
      sessionStorage.removeItem("privacyAcceptedVersion");
      sessionStorage.removeItem("privacyAcceptedAt");
      setProfileNameRaw("");
      localStorage.removeItem("profileNameRaw");
      setProfileName("");
      localStorage.removeItem("profileName");
      setCurrentPage("home");
      setSearchQuery("");
      setSessionId(createClientId());
      setConversationId(null);
      setChatMessages([]);
      setIsChatView(false);
      setIsSidebarOpen(false);
    };

    const scheduleLogout = () => {
      const lastActivityAt = Number(localStorage.getItem(AUTH_LAST_ACTIVITY_KEY) || Date.now());
      const elapsed = Date.now() - lastActivityAt;
      const remaining = AUTH_IDLE_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        runLogout();
        return;
      }

      timeoutId = window.setTimeout(runLogout, remaining);
    };

    const registerActivity = () => {
      localStorage.setItem(AUTH_LAST_ACTIVITY_KEY, String(Date.now()));
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      scheduleLogout();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        registerActivity();
      }
    };

    registerActivity();

    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach((eventName) => {
      window.addEventListener(eventName, registerActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      events.forEach((eventName) => {
        window.removeEventListener(eventName, registerActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated]);

  const handleSidebarNavigate = (pageId) => {
    if (pageId === "home") {
      setCurrentPage("home");
      setSearchQuery("");
      setChatMessages([]);
      setConversationId(null);
      setIsChatView(false);
      setHomeViewKey((previous) => previous + 1);
      return;
    }

    setConversationId(null);
    setSearchQuery("");
    setChatMessages([]);
    setIsChatView(false);
    setCurrentPage(pageId);
  };

  if (!isAuthenticated) {
    if (mustResetPassword) {
      // Show change password directly — privacy statement will show after first real login
      return (
        <ChangePasswordPage
          onPasswordChanged={() => {
            setMustResetPassword(false);
            setIsAuthenticated(false);
          }}
          onLogout={handleLogout}
        />
      );
    }
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <>
      <div className={`app-container${showPrivacyStatement && !privacyReadOnly ? ' app-blurred' : ''}`}>
        <Sidebar
          currentPage={currentPage}
          onNavigate={handleSidebarNavigate}
          isOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
          theme={theme}
          onToggleTheme={handleToggleTheme}
          onLogout={handleLogout}
          isAdmin={(getAuthUser()?.roles || []).includes("admin")}
        />
        <div className="main-content">
          <Header userName={profileName} onNavigateHome={handleBackToHome} />
          <main className="page-content">
            {currentPage === "home" && (
              <React.Fragment key={homeViewKey}>
                {isChatView ? (
                  <ChatAssistantWindow
                    messages={chatMessages}
                    onSubmitQuery={handleSearch}
                    onActionClick={handleChatAction}
                    onClear={handleClearSearch}
                    initialInputValue={searchQuery}
                    onBackHome={handleBackToHome}
                  />
                ) : (
                  <HeroSection
                    onSearch={handleSearch}
                    onQuickAction={handleQuickActionPrefill}
                    searchQuery={searchQuery}
                    onClearSearch={handleClearSearch}
                  />
                )}
                {!isChatView && <ToolsSection onToolSelect={handleToolSelect} />}
              </React.Fragment>
            )}
            {currentPage === "coming-soon" && (
              <ComingSoonPage onBackToHome={handleBackToHome} />
            )}
            {currentPage === "doc-redact" && (
              <DocRedactPage onBackToHome={handleBackToHome} />
            )}
            {currentPage === "docs-compare" && (
              <DocsComparePage onBackToHome={handleBackToHome} />
            )}
            {currentPage === "doc-template-converter" && (
              <DocTemplateConverterPage onBackToHome={handleBackToHome} />
            )}
            {currentPage === "universal-patient-language" && (
              <UniversalPatientLanguagePage onBackToHome={handleBackToHome} />
            )}
            {currentPage === "veeva-gpt" && (
              <VeevaGptPage
                userName={profileName}
                messages={veevaGptMessages}
                onSubmitQuery={handleVeevaGptSubmit}
                onClear={handleVeevaGptNewChat}
                chatHistory={veevaGptChatHistory}
                onNewChat={handleVeevaGptNewChat}
                onSelectChat={handleVeevaGptSelectChat}
                onDeleteChat={handleVeevaGptDeleteChat}
                onRenameChat={handleVeevaGptRenameChat}
                projects={veevaGptProjects}
                onCreateProject={handleVeevaGptCreateProject}
                onDeleteProject={handleVeevaGptDeleteProject}
                onRenameProject={handleVeevaGptRenameProject}
                onNewChatInProject={handleVeevaGptNewChatInProject}
                activeProjectId={veevaGptActiveProjectId}
                availableModels={veevaGptAvailableModels}
                selectedModel={veevaGptSelectedModel}
                onModelChange={setVeevaGptSelectedModel}
              />
            )}
            {currentPage === "vault-crm" && (
              <VaultCRMPage
                onBackToHome={handleBackToHome}
                onStartMigration={handleStartMigration}
              />
            )}
            {currentPage === "migration-wizard" && (
              <MigrationWizard
                key={migrationWizardKey}
                onBack={handleBackToVaultCRM}
                onComplete={handleBackToHome}
              />
            )}
            {currentPage === "admin-users" && (
              <AdminUsersPage onBackToHome={handleBackToHome} />
            )}
          </main>
          <Footer onShowPrivacy={handleShowPrivacyFromFooter} contactEmail={contactEmail} />
        </div>
      </div>
      {showPrivacyStatement && (
        <PrivacyStatement
          onAccept={handlePrivacyAccept}
          onClose={handlePrivacyClose}
          readOnly={privacyReadOnly}
          username={profileNameRaw}
          contactEmail={contactEmail}
        />
      )}
    </>
  );
}

// Wrap with ThemeProvider
function AppHostWithTheme() {
  return (
    <ThemeProvider>
      <AppHost />
    </ThemeProvider>
  );
}

export default AppHostWithTheme;
